import { User } from "../../user/types";
import { getResourceTypeAndPageFromUrlHash } from "../details/dbtDocsUtils";
import { ConversationGroup, ConversationSources } from "./conversations/context/types";
import { getModelPartsFromLocationHash } from "./highlightUtils";
import { LoadingState, NewConversation } from "./types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface Manifest {
  exposures?: Record<string, { name: string; original_file_path: string; unique_id: string }>;
  macros?: Record<string, { name: string; original_file_path: string; unique_id: string }>;
  nodes?: Record<
    string,
    {
      name: string;
      original_file_path: string;
      unique_id: string;
      raw_code: string;
    }
  >;
  sources?: Record<string, { name: string; original_file_path: string; unique_id: string }>;
}
export interface DbtDocsState {
  users: Record<number, User>;
  currentUserId: User["id"] | undefined;
  isRightPanelOpen: boolean;
  selectedConversationId: ConversationGroup["conversation_group_id"] | undefined;
  conversations: Record<number, ConversationGroup>;
  conversationsLoadingState: LoadingState;
  newConversation: NewConversation | undefined;
  shareId: number | undefined;
  docsAppRendered: boolean;
  currentPage: {
    resourceType: string;
    name: string;
  };
  codeblockLoaded: boolean;
  source: ConversationSources;
  manifest: Manifest;
}

export const initialState = {
  users: {},
  isRightPanelOpen: false,
  selectedConversationId: undefined,
  conversations: {},
  conversationsLoadingState: LoadingState.UNINITIALIZED,
  newConversation: undefined,
  shareId: undefined,
  docsAppRendered: false,
  currentPage: getResourceTypeAndPageFromUrlHash(),
  codeblockLoaded: false,
  source: ConversationSources.DBT_DOCS,
  manifest: {},
} as DbtDocsState;

const conversationsSlice = createSlice({
  name: "appState",
  initialState,
  reducers: {
    setDocsAppRendered: (state, action: PayloadAction<DbtDocsState["docsAppRendered"]>) => {
      state.docsAppRendered = action.payload;
    },
    setManifest: (state, action: PayloadAction<DbtDocsState["manifest"]>) => {
      state.manifest = action.payload;
    },
    updateCurrentPage: (state, action: PayloadAction<DbtDocsState["currentPage"]>) => {
      state.currentPage = action.payload;
    },
    setConversationSource: (state, action: PayloadAction<DbtDocsState["source"]>) => {
      state.source = action.payload;
    },
    updateCodeblockLoaded: (state, action: PayloadAction<DbtDocsState["codeblockLoaded"]>) => {
      state.codeblockLoaded = action.payload;
    },
    setShareId: (state, action: PayloadAction<DbtDocsState["shareId"]>) => {
      state.shareId = action.payload;
    },
    setCurrentUserId: (state, action: PayloadAction<DbtDocsState["currentUserId"]>) => {
      state.currentUserId = action.payload;
    },
    setConversationsLoadingState: (
      state,
      action: PayloadAction<DbtDocsState["conversationsLoadingState"]>
    ) => {
      state.conversationsLoadingState = action.payload;
    },
    refetchConversations: state => {
      state.conversationsLoadingState = LoadingState.UNINITIALIZED;
    },
    setUsers: (state, action: PayloadAction<User[] | undefined>) => {
      if (!action.payload?.length) {
        return state;
      }
      return {
        ...state,
        users: action.payload.reduce((acc: Record<string, User>, user) => {
          acc[user.id] = user;
          return acc;
        }, {}),
      };
    },
    updateRightPanelState: (state, action: PayloadAction<DbtDocsState["isRightPanelOpen"]>) => {
      state.isRightPanelOpen = action.payload;
    },
    updateSelectedConversationId: (
      state,
      action: PayloadAction<DbtDocsState["selectedConversationId"]>
    ) => {
      state.selectedConversationId = action.payload;
    },
    upsertConversation: (state, action: PayloadAction<ConversationGroup>) => {
      state.conversations = {
        ...state.conversations,
        [action.payload.conversation_group_id]: {
          ...state.conversations[action.payload.conversation_group_id],
          ...action.payload,
        },
      };
    },
    resolveConversationGroup: (
      state,
      {
        payload: { conversationGroupId },
      }: PayloadAction<{ shareId: number; conversationGroupId: number }>
    ) => {
      if (state.conversations[conversationGroupId]) {
        delete state.conversations[conversationGroupId];
      }
    },
    updateNewConversation: (state, action: PayloadAction<NewConversation>) => {
      const updated = { ...action.payload };
      if (!updated.meta) {
        console.log("Invalid meta");
        return;
      }
      const modelParts = getModelPartsFromLocationHash();
      if (!modelParts || modelParts.length < 3) {
        console.error("Unable to find model parts", modelParts);
        return;
      }

      console.log("model parts found", modelParts);
      updated.meta.uniqueId = modelParts[2];
      updated.meta.resource_type = modelParts[1];
      if (!updated.meta.range) {
        updated.meta.range = {
          end: { line: 0, character: 0 },
          start: { line: 0, character: 0 },
        };
      }
      if (updated.meta.uniqueId) {
        const node =
          state.manifest.nodes?.[updated.meta.uniqueId] ||
          state.manifest.macros?.[updated.meta.uniqueId];
        updated.meta.filePath = node?.original_file_path || "";
      }
      state.newConversation = updated;
    },
    resetNewConversation: state => {
      state.newConversation = undefined;
    },
    setConversations: (state, action: PayloadAction<ConversationGroup[] | undefined>) => {
      if (!action.payload) {
        return;
      }
      state.conversations = action.payload.reduce(
        (acc: Record<string, ConversationGroup>, conversation) => {
          acc[conversation.conversation_group_id] = conversation;
          return acc;
        },
        {}
      );

      // Setting this to default
      state.selectedConversationId =
        state.selectedConversationId || action.payload[0].conversation_group_id;
    },
  },
});

export const {
  setCurrentUserId,
  setShareId,
  updateSelectedConversationId,
  updateRightPanelState,
  setUsers,
  setConversations,
  resetNewConversation,
  updateNewConversation,
  upsertConversation,
  setDocsAppRendered,
  updateCurrentPage,
  updateCodeblockLoaded,
  resolveConversationGroup,
  setConversationsLoadingState,
  refetchConversations,
  setConversationSource,
  setManifest,
} = conversationsSlice.actions;
export default conversationsSlice;
